import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CartDrawer from '../home/components/CartDrawer';
import { useSEO } from '../../hooks/useSEO';

export default function RefundPage() {
  const [activeSection, setActiveSection] = useState('returns');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Return & Refund Policy | Motion Boyz – Streetwear Returns',
    description: 'Read the Motion Boyz Return & Refund Policy. Learn about our 14-day return window, eligible items, exchange process, and how to start a return.',
    canonical: '/refund',
    ogImage: `${siteUrl}/og-image.jpg`,
    ogType: 'article',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'returns',
        'non-returnable',
        'exchanges',
        'refunds',
        'damaged-items',
        'shipping-returns',
        'chargeback',
        'contact',
      ];

      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= 300) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'returns', label: 'Returns' },
    { id: 'non-returnable', label: 'Non-Returnable Items' },
    { id: 'exchanges', label: 'Exchanges' },
    { id: 'refunds', label: 'Refunds' },
    { id: 'damaged-items', label: 'Damaged or Incorrect Items' },
    { id: 'shipping-returns', label: 'Shipping Returns' },
    { id: 'chargeback', label: 'Chargeback Policy' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <CartDrawer />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/58edf040d576283f95ca6ae7d9df1926.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />

        <div className="relative z-10 text-center px-6 max-w-4xl w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 mb-6">
            <i className="ri-arrow-go-back-line text-[#00d4ff] text-sm" />
            <span className="text-[#00d4ff] text-xs tracking-widest uppercase font-semibold">Policy</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            Return &amp; Refund Policy
          </h1>
          <p className="text-gray-400 text-base tracking-wide max-w-2xl mx-auto">
            Please review our policy carefully before making a purchase
          </p>
          <p className="text-[#00d4ff] text-sm mt-4 tracking-wider">
            Last Updated: <span className="font-semibold">February 27, 2026</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[90%] max-w-[1400px] mx-auto py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar Navigation */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-gray-500 text-xs tracking-widest uppercase font-semibold mb-4 px-4">
                Quick Navigation
              </p>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm tracking-wide rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSection === section.id
                      ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-l-2 border-[#00d4ff]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-4xl">

            {/* Intro banner */}
            <div className="mb-12 p-6 rounded-2xl bg-[#00d4ff]/5 border border-[#00d4ff]/15">
              <p className="text-gray-300 text-sm leading-relaxed">
                At <strong className="text-white">Motion Boyz</strong>, we take pride in delivering premium, limited-quantity streetwear. Please review our policy carefully before making a purchase.
              </p>
            </div>

            {/* Returns */}
            <section id="returns" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Returns</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>We accept returns within <strong className="text-white">14 days of delivery</strong>.</p>
                <p className="font-semibold text-white">To be eligible for a return, items must be:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'Unworn',
                    'Unwashed',
                    'Free of stains, odors, or damage',
                    'Tags must be attached',
                    'Original packaging required',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-gray-500 text-sm italic">Items that do not meet these conditions will not be accepted.</p>

                <div className="mt-6 p-5 rounded-xl bg-[#111] border border-[#00d4ff]/10">
                  <p className="text-white font-semibold mb-3 text-sm tracking-wide">To start a return, email us at:</p>
                  <a
                    href="mailto:llc.motionboyz@gmail.com"
                    className="text-[#00d4ff] hover:underline cursor-pointer text-sm"
                  >
                    📩 llc.motionboyz@gmail.com
                  </a>
                  <p className="text-gray-400 text-sm mt-4 mb-2 font-semibold">Include in your email:</p>
                  <ul className="space-y-1 text-sm text-gray-400">
                    {['Order number', 'Full name', 'Reason for return', 'Clear photos (if needed)'].map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <span className="text-[#00d4ff]">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Non-Returnable Items */}
            <section id="non-returnable" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Non-Returnable Items</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Due to the limited nature of our drops, the following are <strong className="text-white">non-returnable</strong>:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {[
                    { icon: 'ri-price-tag-3-line', label: 'Sale items' },
                    { icon: 'ri-fire-line', label: 'Limited drops marked "Final Sale"' },
                    { icon: 'ri-gift-line', label: 'Gift cards' },
                    { icon: 'ri-scissors-line', label: 'Custom pieces' },
                    { icon: 'ri-t-shirt-line', label: 'Worn or washed items' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#111] border border-gray-800"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-500/10 text-red-400">
                        <i className={`${item.icon} text-sm`} />
                      </div>
                      <span className="text-gray-300 text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Exchanges */}
            <section id="exchanges" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Exchanges</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>We currently <strong className="text-white">do not guarantee exchanges</strong> due to limited stock.</p>
                <p>If you need a different size:</p>
                <ol className="space-y-3 mt-2">
                  {[
                    'Submit a return (if eligible)',
                    'Place a new order for the correct size',
                  ].map((step, i) => (
                    <li key={step} className="flex items-start gap-3">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] text-xs font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-sm text-gray-500 italic">
                  We recommend reviewing the size chart carefully before purchasing.
                </p>
              </div>
            </section>

            {/* Refunds */}
            <section id="refunds" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Refunds</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Once your return is received and inspected:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'Approved refunds will be issued to your original payment method',
                    'Please allow 5–10 business days for processing',
                    'Shipping fees are non-refundable',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-gray-500 text-sm italic mt-2">
                  If your return is rejected, the item will be sent back to you.
                </p>
              </div>
            </section>

            {/* Damaged or Incorrect Items */}
            <section id="damaged-items" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Damaged or Incorrect Items</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>If you receive a damaged or incorrect item:</p>
                <ul className="space-y-2 pl-4">
                  {[
                    'Contact us within 48 hours of delivery',
                    'Include clear photos of the issue',
                    'We will resolve the issue immediately',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#00d4ff] mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Shipping Returns */}
            <section id="shipping-returns" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Shipping Returns</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Customers are responsible for return shipping costs unless the item was damaged or incorrect.
                  We recommend using a trackable shipping method.
                </p>
                <p className="text-gray-500 text-sm italic">
                  Motion Boyz is not responsible for lost return packages.
                </p>
              </div>
            </section>

            {/* Chargeback Policy */}
            <section id="chargeback" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Chargeback Policy</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Unauthorized chargebacks will be disputed with proof of delivery and order confirmation.
                  Please contact us directly to resolve any issues <strong className="text-white">before</strong> filing a dispute.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section id="contact" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Contact</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>For all return-related questions:</p>
                <div className="bg-[#111] border border-[#00d4ff]/10 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff]">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Email</p>
                      <a
                        href="mailto:llc.motionboyz@gmail.com"
                        className="text-[#00d4ff] hover:underline cursor-pointer text-sm"
                      >
                        llc.motionboyz@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bottom CTA */}
            <div className="mt-16 p-8 bg-gradient-to-br from-[#00d4ff]/5 to-transparent border border-[#00d4ff]/20 rounded-2xl text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] mx-auto mb-4">
                <i className="ri-shopping-bag-line text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Shop with Confidence</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Explore our exclusive collection of luxury streetwear designed for those who never stop moving.
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/shop')}
                className="px-8 py-3 bg-[#00d4ff] text-black font-bold text-sm tracking-widest uppercase rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 0 30px rgba(0,212,255,0.4)' }}
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
